import {
  ScoreCard,
  ScoreLeft,
  ScoreRight,
} from '@runtime/components/layout/ScoreLayout'
import { Button } from '@runtime/components/ui/Button'
import { Text } from '@runtime/components/ui/Text'
import { ScoreText } from '@runtime/components/ui/StatText'
import type { I18nT } from '@runtime/core/i18n/i18n'
import type {
  GameHistoryEntry,
  ProfileFullGame,
  UserProfile,
} from '@runtime/features/profile/model/profileTypes'
import styles from '@runtime/features/profile/history/tabs/HistoryTab.module.scss'

type HistoryTabProps = {
  t: I18nT
  user: UserProfile | null
  favorites: string[]
  fullGames: Record<string, ProfileFullGame>
  selectedGameId: string | null
  onSelectGame: (game: GameHistoryEntry, canReview: boolean) => void
  onToggleFavorite: (gameId: string) => void
}

function formatGameRow(
  t: I18nT,
  g: GameHistoryEntry,
  fullGame: ProfileFullGame | undefined,
) {
  const date = g.ts ? new Date(g.ts) : null
  const dateStr = date
    ? date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : ''

  const vs = g.opponents && g.opponents.length ? g.opponents.join(', ') : '—'

  let kindLabel = ''
  if (g.kind === 'puzzle') {
    const mode = fullGame?.meta?.mode
    if (mode === 'puzzlePosterSwitch')
      kindLabel = t('profile.history.kindLabels.puzzlePosterSwitch')
    else if (mode === 'puzzlePoster')
      kindLabel = t('profile.history.kindLabels.puzzlePoster')
    else kindLabel = t('profile.history.kindLabels.puzzle')
  } else if (g.kind === 'strategy-multi') {
    kindLabel = t('profile.history.kindLabels.strategyMulti')
  } else if (g.kind === 'strategy-2v2') {
    kindLabel = t('profile.history.kindLabels.strategy2v2')
  } else if (g.vsAI) {
    kindLabel = t('profile.history.kindLabels.strategyVsAi')
  } else {
    kindLabel = t('profile.history.kindLabels.strategy')
  }

  let resLabel = ''
  if (g.result === 'win') resLabel = t('profile.history.resultLabels.win')
  else if (g.result === 'loss')
    resLabel = t('profile.history.resultLabels.loss')
  else if (g.result === 'draw')
    resLabel = t('profile.history.resultLabels.draw')

  return { dateStr, vs, kindLabel, resLabel }
}

export default function HistoryTab({
  t,
  user,
  favorites,
  fullGames,
  selectedGameId,
  onSelectGame,
  onToggleFavorite,
}: HistoryTabProps) {
  return (
    <div className={styles.loginStats}>
      <div className={styles.loginSectionTitle}>
        {t('profile.history.title')}
      </div>
      <Text variant="legend" className={styles.loginHistoryHint}>
        {t('profile.history.hint')}
      </Text>

      <div className={styles.loginHistoryList} data-testid="login-history-list">
        {user?.history && user.history.length > 0 ? (
          user.history.map((g) => {
            const full = fullGames[g.id]
            const { dateStr, vs, kindLabel, resLabel } = formatGameRow(
              t,
              g,
              full,
            )
            const isFav = favorites.includes(g.id)

            const canReview = !!(full && full.board && full.board.length)

            return (
              <ScoreCard
                key={g.id}
                className={[
                  styles.historyRow,
                  selectedGameId === g.id ? styles.historyRowSelected : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onSelectGame(g, canReview)}
              >
                <ScoreLeft>
                  <div>
                    <div>
                      {kindLabel}
                      {!canReview && (
                        <span> · {t('profile.history.noDetails')}</span>
                      )}
                    </div>
                    <div>
                      {dateStr}
                      {vs &&
                        ` · ${t('profile.history.vsLabel', { names: vs })}`}
                      {resLabel && ` · ${resLabel}`}
                    </div>
                  </div>
                </ScoreLeft>
                <ScoreRight>
                  {g.scoreFor != null && (
                    <ScoreText>
                      {g.scoreFor}
                      {g.scoreAgainst != null && '–' + g.scoreAgainst}
                    </ScoreText>
                  )}
                  <Button
                    type="button"
                    size="s"
                    className={[
                      styles.loginFavBtn,
                      isFav ? styles.loginFavBtnActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title={
                      isFav
                        ? t('profile.history.favRemove')
                        : t('profile.history.favAdd')
                    }
                    onClick={(event) => {
                      event.stopPropagation()
                      onToggleFavorite(g.id)
                    }}
                  >
                    ★
                  </Button>
                </ScoreRight>
              </ScoreCard>
            )
          })
        ) : (
          <Text variant="legend">{t('profile.history.empty')}</Text>
        )}
      </div>
    </div>
  )
}
